import type { WorkspacePublicId } from '@hookmyapp/shared';
import { UserBlockingError } from '@hookmyapp/observability/errors';
import { recordPendingState } from '@/lib/meta-popup-context';

type FetchWithAuth = (path: string, options?: RequestInit) => Promise<Response>;

const POPUP_NAME = 'fb_login_signup';

function popupFeatures(): string {
  const w = 600;
  const h = 700;
  const left = Math.round((screen.width - w) / 2);
  const top = Math.round((screen.height - h) / 2);
  return `width=${w},height=${h},left=${left},top=${top},scrollbars=yes`;
}

/**
 * Launch Facebook Login OAuth flow. Calls POST /instagram/facebook-login/oauth/start
 * to mint a server-side state row, then navigates the popup (or falls back to the same
 * window on mobile / popup-blocked) to the returned redirectUrl.
 *
 * Popup-blocker handling: window.open is the synchronous gated action — opened
 * immediately to about:blank, then navigated via popup.location.assign once the
 * mint resolves. This avoids the popup-blocker without requiring fresh user
 * activation on the navigate (MDN transient-activation).
 */
export interface FacebookLoginStart {
  /** Backend start route to POST (mints the state, returns the dialog URL). */
  path: string;
  /** Response key carrying the dialog URL. */
  urlKey: 'redirectUrl' | 'authorizeUrl';
}

const INSTAGRAM_VIA_FACEBOOK_START: FacebookLoginStart = {
  path: '/instagram/facebook-login/oauth/start',
  urlKey: 'redirectUrl',
};

/** Resolves the popup handle (null on the same-window path) so callers can notice it closing. */
export async function launchFacebookLogin(
  workspacePublicId: WorkspacePublicId,
  fetchWithAuth: FetchWithAuth,
  start: FacebookLoginStart = INSTAGRAM_VIA_FACEBOOK_START,
): Promise<Window | null> {
  const isMobile = matchMedia('(max-width: 768px)').matches;
  const popup = isMobile ? null : window.open('about:blank', POPUP_NAME, popupFeatures());

  let redirectUrl: string;
  try {
    const res = await fetchWithAuth(start.path, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-Workspace-Id': workspacePublicId,
      },
      body: JSON.stringify({}),
    });
    if (!res.ok) {
      throw new UserBlockingError({
        message: `${start.path} failed: ${res.status}`,
        code: 'FACEBOOK_LOGIN_OAUTH_START_FAILED',
        details: { status: res.status },
      });
    }
    const mint = (await res.json()) as Record<string, string | undefined>;
    redirectUrl = mint[start.urlKey] ?? '';
    // Mark this tab as the flow's owner so the localStorage relay in
    // useMetaSignupListeners only fires here, not in every open tab.
    if (mint.state) recordPendingState(mint.state);
  } catch (e) {
    popup?.close();
    throw e;
  }

  if (popup) {
    popup.location.assign(redirectUrl);
    return popup;
  }

  // Mobile or popup-blocker — fall through to same-window redirect.
  window.location.assign(redirectUrl);
  return null;
}
